/**
 * FieldSelectDialog — shared select-then-Save dialog for single-field edits
 * (Importer Sync / Employee Status / Employee Role in the Back Office
 * Integration card). Tapping an option stages it; a tinted description panel
 * explains the staged option; Save commits and is disabled until the staged
 * value differs from current (and any required input is filled).
 *
 * Uses the absolute-overlay shell already established by cell-edit-modal.tsx
 * (dimmed backdrop + positioned Box), NOT `@pimm/base`/native-base `Modal` —
 * nesting a Modal inside MemberProfileModal (itself a Modal) creates
 * competing focus traps that steal keystrokes from the Employee ID input.
 *
 * No loading/error state lives here — onSave is a synchronous fire-and-forget
 * callback; the caller owns the async mutation and any success/error banner.
 */
import { ReactNode, useEffect, useState } from 'react';
import { Box, CheckIcon, HStack, InfoOutlineIcon, Pressable, VStack } from 'native-base';
import { Button, InputField, Text } from '@pimm/base';

export type FieldSelectTone = 'accent' | 'warning' | 'danger' | 'success' | 'neutral';

const TONE: Record<FieldSelectTone, { bg: string; text: string }> = {
  accent: { bg: 'blue.100', text: 'blue.700' },
  warning: { bg: 'amber.100', text: 'amber.800' },
  danger: { bg: 'error.100', text: 'error.700' },
  success: { bg: 'success.100', text: 'success.700' },
  neutral: { bg: 'gray.100', text: 'gray.600' },
};

export type FieldSelectOption = {
  value: string;
  label: string;
  leading?: ReactNode;
  description: string;
  tone: FieldSelectTone;
};

export type FieldSelectInput = {
  label: string;
  initialValue: string;
  caution: string;
  required: (selectedValue: string) => boolean;
  resetLabel: string;
};

export type FieldSelectDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  titleIcon?: ReactNode;
  subtitle?: string;
  // 'caption' (default) = small muted note. 'body' = regular paragraph text,
  // for when the subtitle IS the primary explanation (e.g. Promote's full
  // sentence, which reads as cramped/oddly styled at caption size).
  subtitleVariant?: 'caption' | 'body';
  currentValue: string;
  options: FieldSelectOption[];
  onSave: (value: string, inputValue?: string) => void;
  cancelLabel: string;
  saveLabel: string;
  input?: FieldSelectInput;
  width?: number;
  // Confirming the default preselection is itself a valid action (e.g.
  // Promote) — default true, so existing edit-style callers keep requiring
  // a real change before Save enables.
  requireChange?: boolean;
  // Shows a "current" label next to whichever option matches `currentValue`.
  // Off by default — most callers' initial selection already visually reads
  // as "selected"; only needed when currentValue is a default preselection
  // rather than the real current value (e.g. Promote).
  showCurrentBadge?: boolean;
};

export const FieldSelectDialog = ({
  isOpen,
  onClose,
  title,
  titleIcon,
  subtitle,
  subtitleVariant = 'caption',
  currentValue,
  options,
  onSave,
  cancelLabel,
  saveLabel,
  input,
  width = 380,
  requireChange = true,
  showCurrentBadge = false,
}: FieldSelectDialogProps) => {
  const [sel, setSel] = useState(currentValue);
  const [inputVal, setInputVal] = useState(input?.initialValue ?? '');

  useEffect(() => {
    if (isOpen) {
      setSel(currentValue);
      setInputVal(input?.initialValue ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currentValue]);

  if (!isOpen || options.length === 0) return null;

  const staged = options.find(o => o.value === sel) ?? options[0];
  const tone = TONE[staged.tone];

  const modeChanged = sel !== currentValue;
  const showInput = !!input;
  const inputRequired = showInput && !!input && input.required(sel);
  const idChanged = showInput && inputVal.trim() !== (input?.initialValue ?? '').trim();
  const changed = modeChanged || idChanged;
  const inputOk = !inputRequired || inputVal.trim().length > 0;
  const canSave = (requireChange ? changed : true) && inputOk;

  const handleSave = () => {
    if (!canSave) return;
    onSave(sel, showInput ? inputVal.trim() : undefined);
  };

  return (
    <Box position="absolute" top={0} left={0} right={0} bottom={0} zIndex={50}>
      <Pressable position="absolute" top={0} left={0} right={0} bottom={0} bg="rgba(0,0,0,0.5)" onPress={onClose} />
      <Box position="absolute" top="8%" alignSelf="center" bg="white" rounded="xl" shadow={9} w={`${width}px`} maxW="92%" maxH="84%" overflow="hidden">
        <VStack px={5} pt={5} pb={subtitle ? 1 : 3}>
          <HStack space={2} alignItems="center" pr={8}>
            {titleIcon}
            <Text size="xl" fontWeight={700} color="gray.900">
              {title}
            </Text>
          </HStack>
          {!!subtitle && (
            <Text
              size={subtitleVariant === 'body' ? 'md' : 'sm'}
              fontWeight={subtitleVariant === 'body' ? 400 : 500}
              color={subtitleVariant === 'body' ? 'gray.700' : 'gray.500'}
              pb={2}
            >
              {subtitle}
            </Text>
          )}
        </VStack>

        <VStack borderTopWidth={1} borderColor="gray.100">
          {options.map((opt, i) => {
            const isSelected = opt.value === sel;
            const optTone = TONE[opt.tone];
            return (
              <Pressable
                key={opt.value}
                onPress={() => setSel(opt.value)}
                px={5}
                py={4}
                bg={isSelected ? optTone.bg : 'white'}
                _hover={!isSelected ? { bg: 'gray.50' } : undefined}
                _pressed={{ bg: isSelected ? optTone.bg : 'gray.100' }}
                borderBottomWidth={i !== options.length - 1 ? 1 : 0}
                borderColor="gray.100"
              >
                <HStack alignItems="center" space={3} minH={7}>
                  {opt.leading}
                  <Text size="lg" fontWeight={isSelected ? 600 : 400} color={isSelected ? 'gray.900' : 'gray.700'} flex={1}>
                    {opt.label}
                  </Text>
                  {showCurrentBadge && opt.value === currentValue && (
                    <Text size="sm" color="gray.400">
                      current
                    </Text>
                  )}
                  {isSelected && (
                    <HStack w={6} h={6} rounded="full" bg={optTone.text} alignItems="center" justifyContent="center" ml={2}>
                      <CheckIcon size={3} color="white" />
                    </HStack>
                  )}
                </HStack>
              </Pressable>
            );
          })}
        </VStack>

        <Box h={3} bg="white" />
        <VStack px={5} py={4} bg={tone.bg} space={3}>
          <HStack space={2} alignItems="flex-start">
            <InfoOutlineIcon size={5} color={tone.text} mt={0.5} />
            <Text size="md" fontWeight={400} color={tone.text} flex={1}>
              {staged.description}
            </Text>
          </HStack>

          {showInput && input && (
            <VStack space={1}>
              <HStack alignItems="center" justifyContent="space-between">
                <Text size="sm" fontWeight={600} color="gray.700">
                  {input.label}
                  {inputRequired ? ' *' : ''}
                </Text>
                {idChanged && (
                  <Pressable onPress={() => setInputVal(input.initialValue)} px={2} py={1} rounded="sm" _hover={{ bg: 'gray.100' }}>
                    <Text size="sm" fontWeight={600} color="blue.600">
                      {input.resetLabel}
                    </Text>
                  </Pressable>
                )}
              </HStack>
              <InputField value={inputVal} onChangeText={setInputVal} />
              {inputRequired && (
                <Text size="sm" fontWeight={500} color={tone.text}>
                  {input.caution}
                </Text>
              )}
            </VStack>
          )}
        </VStack>

        <HStack space={2} justifyContent="flex-end" px={5} py={4} borderTopWidth={1} borderColor="gray.100">
          <Button alternate outline bg="white" minWidth={110} minH={9} onPress={onClose}>
            {cancelLabel}
          </Button>
          <Button minWidth={110} minH={9} isDisabled={!canSave} onPress={handleSave}>
            {saveLabel}
          </Button>
        </HStack>
      </Box>
    </Box>
  );
};

export default FieldSelectDialog;
